import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navItems = [
    { name: 'Home', path: '/' },
    {
      name: 'For Business',
      dropdown: [
        { name: 'Keynotes', path: '/keynotes' },
        { name: 'Workshops & Training', path: '/workshops' },
        { name: 'Peak 9 Library', path: '/library' },
      ]
    },
    {
      name: 'For Coaches',
      dropdown: [
        { name: 'School of Shayamal', path: '/school' },
      ]
    },
    {
      name: 'For You',
      dropdown: [
        { name: 'Performance by Design', path: '/course', highlight: true },
        { name: 'Peak 9 Library', path: '/library' },
      ]
    },
  ];

  const toggleDropdown = (name) => {
    setActiveDropdown(activeDropdown === name ? null : name);
  };

  return (
    <>
      <nav
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/95 backdrop-blur-md py-4 shadow-sm'
            : 'bg-white/90 backdrop-blur-sm py-6'
        }`}
      >
        <div className="container-peak flex items-center justify-between" ref={dropdownRef}>
          {/* Logo */}
          <Link to="/" data-testid="navbar-logo">
            <span className="font-['Cormorant_Garamond'] text-2xl text-[#1F2A44]">
              Peak<span className="text-[#D97904]">9</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              item.dropdown ? (
                <div key={item.name} className="relative">
                  <button
                    onClick={() => toggleDropdown(item.name)}
                    className="font-['Syne'] text-[11px] uppercase tracking-[0.15em] text-[#5A6B7A] hover:text-[#1F2A44] transition-colors flex items-center gap-1"
                  >
                    {item.name}
                    <ChevronDown size={12} className={`transition-transform ${activeDropdown === item.name ? 'rotate-180' : ''}`} />
                  </button>
                  
                  <AnimatePresence>
                    {activeDropdown === item.name && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-0 mt-4 bg-white shadow-lg border border-[#E6E1DA] min-w-[200px]"
                      >
                        {item.dropdown.map((subItem) => (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            className={`block px-5 py-3 font-['Syne'] text-[11px] uppercase tracking-[0.15em] transition-colors ${
                              subItem.highlight 
                                ? 'text-[#D97904] bg-[#D97904]/5 hover:bg-[#D97904]/10' 
                                : 'text-[#5A6B7A] hover:text-[#D97904] hover:bg-[#F4F1EC]'
                            }`}
                          >
                            {subItem.name}
                            {subItem.highlight && <span className="ml-2 text-[8px] bg-[#D97904] text-white px-1.5 py-0.5 rounded">NEW</span>}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`font-['Syne'] text-[11px] uppercase tracking-[0.15em] transition-colors ${
                    location.pathname === item.path 
                      ? 'text-[#D97904]' 
                      : 'text-[#5A6B7A] hover:text-[#1F2A44]'
                  }`}
                >
                  {item.name}
                </Link>
              )
            ))}
            
            {/* Secondary CTA - Diagnostic Quiz */}
            <Link
              to="/diagnostic"
              className="font-['Syne'] text-[11px] uppercase tracking-[0.15em] text-[#D97904] border border-[#D97904] px-4 py-2 hover:bg-[#D97904] hover:text-white transition-colors"
            >
              Free Quiz
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            className="lg:hidden p-2 text-[#1F2A44]"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-40 pt-24 px-6 lg:hidden bg-white overflow-y-auto"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-6 pb-8">
              {navItems.map((item) => (
                item.dropdown ? (
                  <div key={item.name}>
                    <button
                      onClick={() => toggleDropdown(item.name)}
                      className="font-['Cormorant_Garamond'] text-2xl text-[#1F2A44] flex items-center gap-2 w-full"
                    >
                      {item.name}
                      <ChevronDown size={18} className={`transition-transform ${activeDropdown === item.name ? 'rotate-180' : ''}`} />
                    </button>
                    <AnimatePresence>
                      {activeDropdown === item.name && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="pl-4 mt-3 space-y-3 overflow-hidden"
                        >
                          {item.dropdown.map((subItem) => (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              className="block font-['Syne'] text-sm uppercase tracking-wider text-[#5A6B7A]"
                            >
                              {subItem.name}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`font-['Cormorant_Garamond'] text-2xl ${
                      location.pathname === item.path
                        ? 'text-[#D97904]'
                        : 'text-[#1F2A44]'
                    }`}
                  >
                    {item.name}
                  </Link>
                )
              ))}
              <Link
                to="/diagnostic"
                className="mt-6 bg-[#D97904] text-white font-['Syne'] text-sm uppercase tracking-wider py-4 text-center block"
              >
                Take the Free Quiz
              </Link>
              <a 
                href="https://calendar.app.google/evsn9LsubajagFF3A"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 border border-[#D97904] text-[#D97904] font-['Syne'] text-sm uppercase tracking-wider py-4 text-center block"
              >
                Get in Touch
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
